<template>
    <div>
        <div class="container">
            <div class="grid grid-cols-12">
                <div class="col-span-12 lg:col-span-6">
                    <PageIntro :title="$t('pages.contact-for-schools.title')" />
                    <div class="text-black text-left flex flex-col gap-y-3 mb-12 -mt-12">
                        <span class="font-Open-Sans-Regular"><T t="generic.company-name" /></span>
                        <a
                            :href="$i18n.locales.find((x) => x.code === $i18n.locale).google_maps_url"
                            class="font-Open-Sans-Regular"
                            target="_blank"
                        >
                            <span> <T t="generic.address-title" /></span>:&nbsp;<span class="underline">
                                <T t="generic.address" />
                            </span>
                        </a>
                        <a :href="`tel:${$t('generic.tel-number')}`" class="font-Open-Sans-Regular">
                            <span><T t="generic.tel" /></span>:&nbsp;
                            <span class="underline"><T t="generic.tel-number" /> </span>
                        </a>
                        <a :href="`mailto:${$t('generic.email-address')}`" class="font-Open-Sans-Regular">
                            <span><T t="generic.email" /></span>:&nbsp;
                            <span class="underline"><T t="generic.email-address" /></span>
                        </a>
                    </div>
                </div>
                <div class="col-span-12 lg:col-span-6 -mt-4 mb-6 lg:mt-48">
                    <div class="aspect-w-16 aspect-h-9">
                        <img
                            class="object-cover"
                            :src="require('~/assets/images/contact_illustration.jpg')"
                            alt="Contact illustration"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div ref="contactWrapper" class="pt-8 lg:pt-15 pb-8 lg:pb-36 bg-grey-400 bg-opacity-25">
            <div class="container">
                <div class="grid grid-cols-12 lg:gap-x-9">
                    <div class="col-span-12 lg:col-span-5 relative">
                        <Transition name="fade" mode="out-in">
                            <div
                                v-if="isFeedbackMessageVisible"
                                class="absolute w-full h-full top-0 left-0 bg-[#EFECE8] z-5 flex items-center flex-col"
                            >
                                <img
                                    ref="successMessageIcon"
                                    :src="require('~/assets/images/icons/successful-icon.svg')"
                                    alt="Successful icon"
                                    class="mb-6"
                                />
                                <span class="block font-Exo-Bold mb-4 text-2xl">
                                    <T t="form.contact-feedback-title" />
                                </span>
                                <p class="text-center text-base mb-4">
                                    <T t="form.contact-feedback-subtitle" />
                                </p>
                                <PButton
                                    label="OK"
                                    type="button"
                                    blue
                                    @click.native="isFeedbackMessageVisible = false"
                                />
                            </div>
                        </Transition>
                        <h3 class="section-title-extrabold mb-4 lg:mb-6">
                            <T t="section-title.contact-request" />
                        </h3>
                        <h4 class="font-Open-Sans-Regular mt-4 text-sm lg:text-base mb-12">
                            {{ $t('pages.contact-for-schools.subtitle') }}
                        </h4>
                        <ValidationObserver ref="form" v-slot="{ errors }" slim>
                            <form novalidate class="w-full" @submit.prevent="onSubmit">
                                <div class="flex flex-col gap-y-4.5 lg:gap-y-6">
                                    <ValidationProvider vid="first_name" tag="div">
                                        <Input
                                            v-model="contact_person.first_name"
                                            narrow
                                            :label="$t('form.first-name')"
                                            :is-error-visible="isErrorVisible"
                                            :error="errors.first_name"
                                        />
                                    </ValidationProvider>
                                    <ValidationProvider vid="last_name" tag="div">
                                        <Input
                                            v-model="contact_person.last_name"
                                            narrow
                                            :label="$t('form.last-name')"
                                            :is-error-visible="isErrorVisible"
                                            :error="errors.last_name"
                                        />
                                    </ValidationProvider>
                                    <ValidationProvider vid="school_name" tag="div">
                                        <Input
                                            v-model="school_name"
                                            narrow
                                            :label="$t('form.school-name')"
                                            :is-error-visible="isErrorVisible"
                                            :error="errors.school_name"
                                        />
                                    </ValidationProvider>
                                    <div>
                                        <div class="flex lg:gap-x-4.5 flex-col lg:flex-row lg:items-center">
                                            <span
                                                class="text-sm font-Source-Sans-Pro-Bold whitespace-nowrap w-3/12 lg:text-right mb-3 lg:mb-0 pl-3 lg:pl-0 text-grey-900 uppercase input-label input-label--required"
                                            >
                                                <T t="form.school-address" />
                                            </span>
                                            <div class="flex items-start gap-x-2 lg:gap-x-4 5 lg:pl-0 lg:w-8/12">
                                                <div class="w-6/12 lg:w-4/12 min-w-[4.5rem]">
                                                    <ValidationProvider vid="school_zip_code" tag="div">
                                                        <Input
                                                            v-model="school_address.zip_code"
                                                            :is-error-visible="isErrorVisible"
                                                        />
                                                    </ValidationProvider>
                                                </div>
                                                <div class="w-6/12 lg:w-9/12 min-w-[8.5rem]">
                                                    <ValidationProvider vid="school_address" tag="div">
                                                        <Input
                                                            v-model="school_address.street"
                                                            :is-error-visible="isErrorVisible"
                                                        />
                                                    </ValidationProvider>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="w-full lg:w-8/12 mt-1 ml-auto pl-4.5">
                                            <InputError
                                                :errors="errors.school_zip_code"
                                                :is-error-visible="isErrorVisible"
                                            />
                                            <InputError
                                                :errors="errors.school_address"
                                                :is-error-visible="isErrorVisible"
                                            />
                                        </div>
                                    </div>

                                    <ValidationProvider vid="email" tag="div">
                                        <Input
                                            v-model="email"
                                            narrow
                                            :label="$t('form.email')"
                                            :is-error-visible="isErrorVisible"
                                            :error="errors.email"
                                        />
                                    </ValidationProvider>
                                    <ValidationProvider vid="phone" tag="div">
                                        <Input
                                            v-model="phone"
                                            narrow
                                            :label="$t('form.tel')"
                                            placeholder="+36 22 511 050"
                                            :error="errors.phone"
                                            :is-error-visible="isErrorVisible"
                                        />
                                    </ValidationProvider>
                                    <ValidationProvider vid="school_person_count" tag="div">
                                        <Input
                                            v-model="school_person_count"
                                            sm
                                            :label="$t('form.how-many-people')"
                                            :required="false"
                                            :is-error-visible="isErrorVisible"
                                            :error="errors.school_person_count"
                                        />
                                    </ValidationProvider>
                                    <ValidationProvider vid="message" tag="div">
                                        <Input
                                            v-model="message"
                                            narrow
                                            is-textarea
                                            :error="errors.message"
                                            :is-error-visible="isErrorVisible"
                                            :label="$t('form.message')"
                                        />
                                    </ValidationProvider>
                                    <ValidationProvider vid="privacy" tag="div">
                                        <div class="flex flex-col lg:w-9/12 ml-auto">
                                            <div class="p-field flex items-start lg:pl-6">
                                                <Checkbox
                                                    id="privacy_policy_cbx"
                                                    v-model="privacy"
                                                    class="mt-1"
                                                    :binary="true"
                                                />
                                                <label
                                                    for="privacy_policy_cbx"
                                                    class="text-black text-sm font-Source-Sans-Pro-Regular"
                                                    v-html="$t('form.terms-of-use')"
                                                >
                                                </label>
                                            </div>
                                            <InputError class="lg:ml-8 mt-1" :errors="errors.privacy" />
                                        </div>
                                    </ValidationProvider>
                                    <div class="lg:w-9/12 mx-auto lg:mx-0 lg:ml-auto text-center mt-2">
                                        <PButton
                                            :disabled="isSubmitting"
                                            :label="$t('form.send')"
                                            type="submit"
                                            lg
                                            blue
                                        />
                                    </div>
                                </div>
                            </form>
                        </ValidationObserver>
                    </div>
                    <div class="col-span-12 lg:col-span-6 lg:col-start-7 mt-16 lg:mt-0">
                        <h3 class="section-title-extrabold mb-4.5 lg:mb-9">
                            <T t="section-title.information-for-schools" />
                        </h3>
                        <div
                            class="tinymce-content mb-9 lg:mb-18 text-sm lg:text-base leading-5 lg:leading-6"
                            v-html="$t('generic.information-for-schools-desc')"
                        ></div>
                        <h3 class="section-title-extrabold mb-4">
                            <T t="section-title.school-catalogue.title" />
                        </h3>
                        <h4 class="mb-4.5 lg:mb-9 text-sm lg:text-base">
                            <T t="section-title.school-catalogue.subtitle" />
                        </h4>
                        <div class="flex lg:items-center flex-col lg:flex-row lg:gap-x-4.5">
                            <img
                                class="w-44 h-auto"
                                :src="require(`~/assets/images/catalog-covers/${$i18n.locale}/catalog-thumbnail.jpg`)"
                                alt="Katalógus"
                            />
                            <div class="mt-2 lg:mt-0">
                                <h5 class="text-sm lg:w-7/12">
                                    <T t="section-title.school-catalogue.description" />
                                </h5>
                                <div class="mt-2 lg:mt-4 flex flex-col gap-y-4">
                                    <PButton
                                        :to="`/pdf/${$i18n.locale}/2026_catalog_for_schools.pdf`"
                                        external
                                        download
                                        :label="$t('generic.download')"
                                        appearance="download-link"
                                    />
                                    <PButton
                                        external
                                        :to="`/pdf/${$i18n.locale}/2026_catalog_for_schools.pdf`"
                                        target="_blank"
                                        :label="$t('generic.view')"
                                        appearance="download-link"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ContactForSchools',
    data() {
        return {
            isErrorVisible: false,
            contact_person: {
                first_name: null,
                last_name: null,
            },
            school_name: null,
            school_address: {
                zip_code: null,
                street: null,
            },
            phone: null,
            school_person_count: null,
            email: null,
            message: null,
            privacy: false,
            isFeedbackMessageVisible: false,
            isSubmitting: false,
        };
    },
    watch: {
        isFeedbackMessageVisible(value) {
            if (window.innerWidth < 1024) {
                return;
            }

            if (value) {
                setTimeout(() => {
                    window.scrollTo({
                        top: this.$refs.successMessageIcon.getBoundingClientRect().y - 100,
                        left: 0,
                        behavior: 'smooth',
                    });
                }, 200);
            } else {
                window.scrollTo({
                    top: this.$refs.contactWrapper.offsetTop - 50,
                    left: 0,
                    behavior: 'smooth',
                });
            }
        },
    },
    async mounted() {
        await this.$recaptcha.init();
    },
    beforeDestroy() {
        this.$recaptcha.destroy();
    },
    methods: {
        scrollToTop() {
            window.scrollTo(0, 0);
        },
        async onSubmit() {
            this.isErrorVisible = true;
            this.isSubmitting = true;

            try {
                // 'contact' will be a label that is used in the recaptcha admin panel
                const token = await this.$recaptcha.execute('contact');

                await this.$axios
                    .$post(
                        '/recaptcha/validate/contact/create',
                        {
                            first_name: this.contact_person.first_name,
                            last_name: this.contact_person.last_name,
                            email: this.email,
                            phone: this.phone,
                            school_name: this.school_name,
                            school_zip_code: this.school_address.zip_code,
                            school_address: this.school_address.street,
                            school_person_count: this.school_person_count,
                            message: this.message,
                            privacy: this.privacy ? 1 : 0,
                        },
                        {
                            baseURL: '',
                            headers: {
                                'base-url': this.$axios.defaults.baseURL,
                                'recaptcha-token': token,
                            },
                        }
                    )
                    .then((res) => {
                        if (res.success === 1) {
                            this.contact_person.first_name = '';
                            this.contact_person.last_name = '';
                            this.email = '';
                            this.phone = '';
                            this.school_name = '';
                            this.school_address.zip_code = '';
                            this.school_address.street = '';
                            this.school_person_count = '';
                            this.message = '';
                            this.privacy = 0;
                            this.isFeedbackMessageVisible = true;
                            this.scrollToTop();

                            window.dataLayer.push({ event: 'contact_form_submit' });
                        } else {
                            this.$refs.form.setErrors(res.error);
                        }
                    })
                    .finally(() => {
                        this.isSubmitting = false;
                    });

                await this.$recaptcha.reset();
            } catch (err) {
                this.isSubmitting = false;
                console.log(err);
            }
        },
    },
};
</script>
